import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { useRequirements } from "@mibuildweb/hooks";
import { IAddRequirementsData } from "../schema/addRequirements.schema";

const toApiPayload = (data: IAddRequirementsData) => ({
  name: data.name,
  type: data.type,
  communityId: data.communityId,
  inspectionAuthorityId: data.inspectionAuthorityId,
  accountId: data.accountId,
  materials: (data.materialIds ?? []).map((materialId) => ({
    materialId,
    areaTypeIds: data.materialAreas?.[materialId] ?? [],
    quantity: data.materialQuantities?.[materialId] ?? 1,
  })),
  steps: data.steps ?? [],
});

export const useAddRequirementsViewModel = ({
  goBack = () => null,
  reset = (_data: IAddRequirementsData) => null,
  trigger = () => null,
}: IUseAddRequirementsViewModelProps = {}) => {
  const stateManager = useComponentState();
  const {
    createRequirement,
    updateRequirement,
    isLoading: isRequirementsLoading,
  } = useRequirements();

  const fetchRequirementDetails = async (id: string) => {
    const response = await stateManager.run(() =>
      homeService.requirements.getById({ id })
    );
    if (response.status === HTTP_STATUSES.OK) {
      const { data } = response;
      const materials: any[] = data.materials ?? [];
      reset({
        id: data.id,
        name: data.name,
        type: data.type,
        communityId: data.communityId ?? null,
        inspectionAuthorityId: data.inspectionAuthorityId ?? null,
        accountId: data.accountId ?? null,
        modifiedOn: data.modifiedOn,
        materialIds: materials.map((m) => m.materialId),
        materialAreas: Object.fromEntries(
          materials.map((m) => [m.materialId, m.areaTypeIds ?? []])
        ),
        materialQuantities: Object.fromEntries(
          materials.map((m) => [m.materialId, m.quantity ?? 1])
        ),
        steps: (data.steps ?? []).map((s: any) => ({
          stepTemplateId: s.stepTemplateId,
          workBaseTemplateId: s.workBaseTemplateId,
        })),
      });
      trigger();
    }
  };

  const handleCreate = async (data: IAddRequirementsData) => {
    const response = await createRequirement(toApiPayload(data) as any);
    if (response?.status === HTTP_STATUSES.OK) {
      goBack();
    }
  };

  const handleUpdate = async (data: IAddRequirementsData) => {
    const response = await updateRequirement({
      ...toApiPayload(data),
      id: data.id,
      modifiedOn: data.modifiedOn,
    } as any);
    if (response?.status === HTTP_STATUSES.OK) {
      goBack();
    }
  };

  return {
    isLoading: stateManager.isBusy() || isRequirementsLoading,
    fetchRequirementDetails,
    handleCreate,
    handleUpdate,
  };
};

interface IUseAddRequirementsViewModelProps {
  goBack?: () => void;
  reset?: (data: IAddRequirementsData) => void;
  trigger?: () => void;
}
